import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import AirRoundedIcon from '@mui/icons-material/AirRounded';
import ElectricBoltRoundedIcon from '@mui/icons-material/ElectricBoltRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import SensorsRoundedIcon from '@mui/icons-material/SensorsRounded';
import { sensorsUrl } from '../api/endpoints';
import { STATIC_ICONS } from '../Constants';
import { CustomSwitch } from './Switch';
import {
  Co2StatusIcon,
  HumidityStatusIcon,
  TemperatureStatusIcon,
} from '../svg/EnvironmentalStatusIcons';
import './EnvironmentalStatus.css';
import {
  ENVIRONMENTAL_STATUS_ACCENTS,
  getEnvironmentalAccent,
  inferEnvironmentalMetricKind,
  normalizeEnvironmentalIconType,
} from './environmentalStatusConfig';
import { resolveValueColorFromStops } from './liquidShared';

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const toFiniteNumber = (value, fallback = 0) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
};

const hasMeaningfulValue = (value) => {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.trim() !== '';
  return true;
};

const formatMetricValue = (value, decimals = 0) => {
  if (!hasMeaningfulValue(value)) return '--';

  const numericValue = Number(value);
  const normalizedDecimals = Number(decimals);

  if (Number.isFinite(numericValue)) {
    if (Number.isFinite(normalizedDecimals)) {
      return Number(numericValue.toFixed(Math.max(0, normalizedDecimals))).toString();
    }
    return numericValue.toString();
  }

  return String(value);
};

const parseBooleanValue = (value, fallback = false) => {
  if (typeof value === 'boolean') return value;
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'number') return value !== 0;

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (!normalized) return fallback;
    if (['false', '0', 'no', 'off'].includes(normalized)) return false;
    if (['true', '1', 'yes', 'on'].includes(normalized)) return true;
  }

  return Boolean(value);
};

const estimateTextWidth = (text, fontSize) => {
  const content = String(text || '');
  if (!content) return 0;

  if (typeof document === 'undefined') {
    return content.length * fontSize * 0.54;
  }

  if (!estimateTextWidth.canvas) {
    estimateTextWidth.canvas = document.createElement('canvas');
  }

  const context = estimateTextWidth.canvas.getContext('2d');
  if (!context) {
    return content.length * fontSize * 0.54;
  }

  context.font = `400 ${fontSize}px Lato, Roboto, sans-serif`;
  return context.measureText(content).width;
};

const getCompactMetricLabel = (metric) => {
  const label = String(metric?.label || '').trim();
  const iconType = normalizeEnvironmentalIconType(metric?.iconType);
  const lower = label.toLowerCase();

  if (iconType === 'temperature' || lower.includes('temp')) return 'T';
  if (iconType === 'humidity' || lower.includes('humid')) return 'H';
  if (iconType === 'co2' || lower.includes('co2') || lower.includes('co₂')) return 'CO2';
  if (label) return label.slice(0, 3).toUpperCase();
  return '';
};

const getDatastreamIconCandidate = (datastream) => {
  if (!datastream || typeof datastream !== 'object') return null;

  return (
    datastream.iconSvg ||
    datastream.icon ||
    datastream.iconUrl ||
    datastream.imageUrl ||
    datastream.image ||
    datastream.iconPath ||
    null
  );
};

const resolveMetricPartColor = ({ metric, part, value, fallback }) => {
  const fixedColor = String(metric?.[`${part}Color`] || '').trim();
  return resolveValueColorFromStops({
    value,
    baseColor: fixedColor || fallback,
    stops: metric?.[`${part}Colors`],
    enabled: metric?.[`${part}ChangeColorBasedOnValue`] === true,
    grading: metric?.[`${part}ColorGrading`] === true,
  }) || fixedColor || fallback;
};

const colorWithAlpha = (color, alphaHex) => {
  const value = String(color || '').trim();
  if (/^#[0-9a-f]{6}$/i.test(value)) return `${value}${alphaHex}`;
  if (/^#[0-9a-f]{3}$/i.test(value)) {
    return `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}${alphaHex}`;
  }
  return value;
};

const resolveIconUrl = (iconValue) => {
  if (typeof iconValue !== 'string') return '';
  const trimmed = iconValue.trim();
  if (!trimmed || trimmed.startsWith('<svg')) return '';
  if (
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://') ||
    trimmed.startsWith('data:')
  ) {
    return trimmed;
  }
  if (trimmed.startsWith('icons/')) {
    return STATIC_ICONS + trimmed.replace(/^icons\//, '');
  }
  return STATIC_ICONS + trimmed;
};

const resolveMetricDatastream = ({ metric, devices, datastreams }) => {
  const rootDatastreams = Array.isArray(datastreams) ? datastreams.filter(Boolean) : [];
  const configuredDeviceId = Number(metric?.deviceId);
  let targetDatastreams = rootDatastreams;

  if (configuredDeviceId > 0) {
    const matchedDevice = (devices || []).find(
      (device) => Number(device?.id) === configuredDeviceId
    );

    if (matchedDevice) {
      targetDatastreams = (matchedDevice?.template?.datastreams || []).filter(Boolean);
    }
  }

  return (
    targetDatastreams.find((datastream) => {
      if (metric?.dataId !== null && metric?.dataId !== undefined && metric?.dataId !== '') {
        return Number(datastream?.id) === Number(metric.dataId);
      }

      if (metric?.pin) {
        return String(datastream?.pin || '') === String(metric.pin);
      }

      return false;
    }) || null
  );
};

const MetricIcon = ({ iconType, datastreamIcon, accentColor, label }) => {
  const normalizedIconType = normalizeEnvironmentalIconType(iconType);

  if (typeof datastreamIcon === 'string' && datastreamIcon.trim().startsWith('<svg')) {
    return (
      <span
        className="environmental-status-widget__iconInline"
        aria-label={label}
        dangerouslySetInnerHTML={{ __html: datastreamIcon }}
      />
    );
  }

  const iconUrl = resolveIconUrl(datastreamIcon);
  if (iconUrl) {
    return (
      <img
        className="environmental-status-widget__iconImage"
        src={iconUrl}
        alt={label}
        draggable={false}
      />
    );
  }

  if (normalizedIconType === 'temperature') {
    return <TemperatureStatusIcon className="environmental-status-widget__iconSvg" />;
  }

  if (normalizedIconType === 'humidity') {
    return <HumidityStatusIcon className="environmental-status-widget__iconSvg" />;
  }

  if (normalizedIconType === 'co2') {
    return <Co2StatusIcon className="environmental-status-widget__iconSvg" />;
  }

  const commonProps = {
    className: 'environmental-status-widget__fallbackIcon',
    style: { color: accentColor },
  };

  if (normalizedIconType === 'air') return <AirRoundedIcon {...commonProps} />;
  if (normalizedIconType === 'light') return <LightModeRoundedIcon {...commonProps} />;
  if (normalizedIconType === 'power') return <ElectricBoltRoundedIcon {...commonProps} />;

  return <SensorsRoundedIcon {...commonProps} />;
};

const EnvironmentalStatus = ({
  id,
  title,
  transparentBackground,
  draggable,
  dims,
  metrics,
  controlDatastream,
  showControlSwitch = true,
  devices,
  datastreams,
  did,
  tid,
  borderRadius = 24,
  titleSize = 20,
  datastreamTitleSize = 16,
  valueSize = 31,
  titleColor,
  labelFontSize,
  valueFontSize,
}) => {
  const widgetRef = useRef(null);
  const [measuredDims, setMeasuredDims] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const element = widgetRef.current;
    if (!element) return undefined;

    const updateSize = (width, height) => {
      setMeasuredDims((prev) => {
        if (Math.abs(prev.width - width) < 1 && Math.abs(prev.height - height) < 1) {
          return prev;
        }

        return { width, height };
      });
    };

    const rect = element.getBoundingClientRect();
    updateSize(rect.width || 0, rect.height || 0);

    if (typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        updateSize(width || 0, height || 0);
      }
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const resolvedMetrics = useMemo(
    () =>
      (Array.isArray(metrics) ? metrics : [])
        .map((metric, index) => {
          const safeMetric = metric || {};
          const datastream = resolveMetricDatastream({
            metric: safeMetric,
            devices,
            datastreams,
          });

          const inferredKind = inferEnvironmentalMetricKind(
            safeMetric?.label,
            datastream?.name,
            safeMetric?.unit,
            datastream?.unit,
            safeMetric?.iconType
          );

          const metricKind =
            safeMetric?.iconType && safeMetric.iconType !== 'auto'
              ? normalizeEnvironmentalIconType(safeMetric.iconType)
              : inferredKind;

          const accentColor =
            getEnvironmentalAccent(metricKind, datastream?.color) ||
            ENVIRONMENTAL_STATUS_ACCENTS.generic;

          const liveValue = hasMeaningfulValue(datastream?.value)
            ? datastream.value
            : safeMetric?.defaultValue ?? datastream?.defaultValue ?? '';
          const numericValue = Number(liveValue);
          const colorValue = Number.isFinite(numericValue) ? numericValue : liveValue;

          const unit =
            String(safeMetric?.unit ?? '').trim() || String(datastream?.unit ?? '').trim();

          const label =
            String(safeMetric?.label ?? '').trim() ||
            String(datastream?.name ?? '').trim() ||
            `Datastream ${index + 1}`;

          return {
            id: safeMetric?.id || `environmental-metric-${index}`,
            enabled: typeof safeMetric?.enabled === 'boolean' ? safeMetric.enabled : true,
            label,
            unit,
            accentColor,
            labelBackgroundColor: resolveMetricPartColor({
              metric: safeMetric,
              part: 'labelBackground',
              value: colorValue,
              fallback: colorWithAlpha(accentColor, '26'),
            }),
            labelColor: resolveMetricPartColor({
              metric: safeMetric,
              part: 'label',
              value: colorValue,
              fallback: accentColor,
            }),
            valueColor: resolveMetricPartColor({
              metric: safeMetric,
              part: 'value',
              value: colorValue,
              fallback: 'var(--widget-title-color, rgba(255, 255, 255, 0.97))',
            }),
            unitColor: resolveMetricPartColor({
              metric: safeMetric,
              part: 'unit',
              value: colorValue,
              fallback: accentColor,
            }),
            iconColor: resolveMetricPartColor({
              metric: safeMetric,
              part: 'icon',
              value: colorValue,
              fallback: accentColor,
            }),
            iconBackgroundColor: resolveMetricPartColor({
              metric: safeMetric,
              part: 'iconBackground',
              value: colorValue,
              fallback: colorWithAlpha(accentColor, '16'),
            }),
            value: formatMetricValue(liveValue, safeMetric?.decimals),
            iconType: metricKind,
            datastreamIcon: getDatastreamIconCandidate(datastream),
          };
        })
        .filter((metric) => metric.enabled),
    [datastreams, devices, metrics]
  );

  const resolvedControlDatastream = useMemo(() => {
    if (!controlDatastream || typeof controlDatastream !== 'object') return null;

    return resolveMetricDatastream({
      metric: controlDatastream,
      devices,
      datastreams,
    });
  }, [controlDatastream, datastreams, devices]);

  const [controlChecked, setControlChecked] = useState(false);

  useEffect(() => {
    const liveControlValue = hasMeaningfulValue(resolvedControlDatastream?.value)
      ? resolvedControlDatastream.value
      : controlDatastream?.defaultValue ?? resolvedControlDatastream?.defaultValue ?? 0;

    setControlChecked(parseBooleanValue(liveControlValue));
  }, [
    controlDatastream?.defaultValue,
    resolvedControlDatastream?.defaultValue,
    resolvedControlDatastream?.value,
  ]);

  const widgetDims = useMemo(() => {
    const liveWidth =
      Number.isFinite(measuredDims.width) && measuredDims.width > 0
        ? measuredDims.width
        : Number(dims?.width) || 0;
    const liveHeight =
      Number.isFinite(measuredDims.height) && measuredDims.height > 0
        ? measuredDims.height
        : Number(dims?.height) || 0;

    const width = Math.max(120, liveWidth || 382);
    const height = Math.max(96, liveHeight || 155);
    const metricCount = Math.max(resolvedMetrics.length, 1);
    const safeTitleSize = Math.max(12, toFiniteNumber(labelFontSize, toFiniteNumber(titleSize, 20)));
    const safeDatastreamTitleSize = Math.max(10, toFiniteNumber(datastreamTitleSize, 16));
    const safeValueSize = Math.max(1, toFiniteNumber(valueFontSize, toFiniteNumber(valueSize, 31)));
    const safeBorderRadius = Math.max(0, toFiniteNumber(borderRadius, 24));

    const isTiny = width <= 330 || height <= 136;
    const isMicro = width <= 260 || height <= 118;
    const isCompact = width < 430 || height < 180;

    let columns = metricCount;
    if (metricCount <= 1) {
      columns = 1;
    } else if (isTiny) {
      columns = Math.min(3, metricCount);
    } else {
      columns = Math.min(3, metricCount);
    }

    const gap = Math.round(clamp(width * 0.026, isMicro ? 3 : isTiny ? 5 : 8, isMicro ? 6 : isTiny ? 8 : isCompact ? 12 : 16));
    const paddingX = Math.round(clamp(width * 0.075, isMicro ? 8 : isTiny ? 10 : 26, isMicro ? 12 : isTiny ? 16 : isCompact ? 30 : 38));
    const paddingY = Math.round(clamp(height * 0.17, isMicro ? 8 : isTiny ? 10 : 24, isMicro ? 12 : isTiny ? 16 : isCompact ? 28 : 34));
    const topbarGap = Math.round(
      clamp(
        safeTitleSize * (isTiny ? 0.8 : 1.2),
        isMicro ? 4 : isTiny ? 7 : 16,
        22
      )
    );
    const titleFontSize = Math.round(
      clamp(
        safeTitleSize * (isTiny ? 1 : 1),
        isMicro ? 10 : 12,
        isTiny ? 15 : 22
      )
    );
    const rows = Math.max(1, Math.ceil(metricCount / columns));
    const topbarHeight = Math.max(Math.round(titleFontSize * 1.25), isTiny ? 18 : 22);
    const availableMetricsHeight = Math.max(
      58,
      height - (paddingY * 2) - topbarHeight - topbarGap
    );
    const metricCellHeight = Math.max(
      isMicro ? 42 : isTiny ? 50 : 68,
      (availableMetricsHeight - (gap * Math.max(0, rows - 1))) / rows
    );
    const perColumnWidth = Math.max(34, (width - (paddingX * 2) - (gap * (columns - 1))) / columns);
    const metricLabelFontSize = Math.round(
      clamp(
        safeDatastreamTitleSize * (isMicro ? 0.62 : isTiny ? 0.76 : isCompact ? 0.95 : 1),
        isMicro ? 8 : 10,
        Math.max(isMicro ? 9 : 11, Math.min(perColumnWidth * (isMicro ? 0.25 : 0.18), 17))
      )
    );
    const bubbleSize = Math.round(
      clamp(
        metricLabelFontSize * (isMicro ? 2.35 : isTiny ? 2.2 : 2.35),
        isMicro ? 24 : isTiny ? 28 : 34,
        isMicro ? 30 : isTiny ? 34 : 42
      )
    );
    const iconSize = Math.round(
      clamp(
        bubbleSize * 0.82,
        isMicro ? 21 : isTiny ? 23 : 25,
        isMicro ? 27 : isTiny ? 30 : 34
      )
    );
    const labelToIconGap = Math.round(clamp(metricLabelFontSize * 0.5, isMicro ? 2 : 5, isMicro ? 4 : 8));
    const maxValueByWidth = perColumnWidth * (isMicro ? 0.98 : isTiny ? 0.82 : 0.78);
    const metricTopGap = Math.round(
      clamp(
        metricLabelFontSize * (isMicro ? 0.22 : isTiny ? 0.32 : 0.48),
        isMicro ? 2 : 4,
        8
      )
    );
    const labelHeight = Math.round(
      clamp(
        Math.max(metricLabelFontSize * 1.55, bubbleSize * 0.82),
        22,
        isMicro ? 22 : isTiny ? 26 : 32
      )
    );
    const availableValueHeight = Math.max(
      22,
      metricCellHeight - labelHeight - metricTopGap
    );
    const rowDensityScale = rows <= 1 ? 1 : rows === 2 ? 0.84 : 0.68;
    const maxValueByHeight = Math.max(
      24,
      availableValueHeight * (isTiny ? 0.92 : 0.98)
    );
    const metricValueFontSize = Math.round(
      clamp(
        safeValueSize * (isMicro ? 0.54 : isTiny ? 0.68 : isCompact ? 0.94 : 1) * rowDensityScale,
        isMicro ? 6 : 8,
        Math.max(isMicro ? 8 : 10, Math.min(maxValueByWidth, maxValueByHeight, isMicro ? 24 : isTiny ? 30 : 36))
      )
    );
    const unitFontSize = Math.round(
      clamp(
        Math.min(metricValueFontSize * 0.58, availableValueHeight * 0.55),
        isMicro ? 5 : 6,
        isMicro ? 14 : 21
      )
    );
    const firstLabel = resolvedMetrics[0]?.label || 'Temperature';
    const idealLabelWidth = estimateTextWidth(firstLabel, metricLabelFontSize) + (isTiny ? 20 : 24);
    const labelWidth = Math.round(
      clamp(
        idealLabelWidth,
        Math.max(isMicro ? 22 : 58, metricLabelFontSize * (isMicro ? 1.4 : 4.2)),
        Math.max(isMicro ? 26 : 64, Math.min(perColumnWidth - bubbleSize - labelToIconGap, isMicro ? 44 : isTiny ? 76 : 96))
      )
    );
    const cardRadius = Math.round(clamp(safeBorderRadius, 0, 64));
    const surfaceRadius = Math.round(clamp(safeBorderRadius * 0.5, 0, 32));
    return {
      width,
      height,
      isTiny,
      isMicro,
      isCompact,
      columns,
      rows,
      cardRadius: `${cardRadius}px`,
      surfaceRadius: `${surfaceRadius}px`,
      paddingX: `${paddingX}px`,
      paddingY: `${paddingY}px`,
      topbarGap: `${topbarGap}px`,
      gridGap: `${gap}px`,
      titleFontSize: `${titleFontSize}px`,
      titleMaxWidth: '100%',
      metricPaddingX: '0px',
      dividerInset: isTiny ? '0px' : '8px',
      labelToIconGap: `${labelToIconGap}px`,
      labelFontSize: `${metricLabelFontSize}px`,
      labelHeight: `${labelHeight}px`,
      labelWidth: `${labelWidth}px`,
      labelPaddingX: isMicro ? '6px' : isTiny ? '8px' : '12px',
      labelPaddingY: isMicro ? '3px' : isTiny ? '5px' : '6px',
      bubbleSize: `${bubbleSize}px`,
      iconSize: `${iconSize}px`,
      valueFontSize: `${metricValueFontSize}px`,
      unitFontSize: `${unitFontSize}px`,
      metricTopGap: `${metricTopGap}px`,
      metricValueGap: isTiny ? '1px' : '4px',
      switchScale: isMicro ? 0.42 : isTiny ? 0.5 : isCompact ? 0.6 : 0.68,
      abbreviateLabels: isTiny || perColumnWidth < 102,
    };
  }, [
    borderRadius,
    datastreamTitleSize,
    dims?.width,
    dims?.height,
    labelFontSize,
    measuredDims.height,
    measuredDims.width,
    resolvedMetrics,
    titleSize,
    valueFontSize,
    valueSize,
  ]);

  const widgetTitle = String(title || '').trim() || 'Environmental Status';
  const hasMetrics = resolvedMetrics.length > 0;
  const showDividers = widgetDims.columns > 1;
  const controlPin = controlDatastream?.pin || resolvedControlDatastream?.pin || '';
  const canControl = Boolean(showControlSwitch && controlPin && (did || controlDatastream?.deviceId));
  const handleControlSwitchChange = (checked) => {
    if (!canControl) return;

    setControlChecked(checked);

    const body = JSON.stringify({
      device: controlDatastream?.deviceId || did,
      template: controlDatastream?.templateId || tid,
      data: { [controlPin]: Number(checked).toString() },
    });

    axios.post(
      sensorsUrl('control'),
      body,
      { headers: { 'Content-Type': 'application/json' } }
    ).catch(() => {
      const fallbackValue = hasMeaningfulValue(resolvedControlDatastream?.value)
        ? resolvedControlDatastream.value
        : resolvedControlDatastream?.defaultValue ?? 0;
      setControlChecked(parseBooleanValue(fallbackValue));
    });
  };
  const metricLayoutSpans = useMemo(() => {
    const metricCount = resolvedMetrics.length;
    const columnCount = Math.max(1, Number(widgetDims.columns) || 1);
    const spans = Array(metricCount).fill(1);

    if (metricCount === 0 || columnCount <= 1) {
      return spans;
    }

    const lastRowCount = metricCount % columnCount;
    if (lastRowCount === 0) {
      return spans;
    }

    const lastRowStart = metricCount - lastRowCount;
    let remainingSlots = columnCount;
    let remainingItems = lastRowCount;

    for (let index = lastRowStart; index < metricCount; index += 1) {
      const span = Math.max(1, Math.ceil(remainingSlots / remainingItems));
      spans[index] = span;
      remainingSlots -= span;
      remainingItems -= 1;
    }

    return spans;
  }, [resolvedMetrics.length, widgetDims.columns]);

  return (
    <div
      ref={widgetRef}
      className={[
        'widget-container',
        'environmental-status-widget',
        transparentBackground ? 'is-transparent' : '',
        widgetDims.isMicro ? 'environmental-status-widget--micro' : '',
        widgetDims.isTiny ? 'environmental-status-widget--tiny' : '',
        widgetDims.isCompact ? 'environmental-status-widget--compact' : '',
      ].filter(Boolean).join(' ')}
      draggable={draggable}
      onMouseDown={(e) => {
        if (draggable) e.currentTarget.style.cursor = 'grabbing';
      }}
      onMouseUp={(e) => {
        e.currentTarget.style.cursor = draggable ? 'grab' : 'default';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.cursor = draggable ? 'grab' : 'default';
      }}
      onDragStart={(e) => {
        const widgetWrapper = e.currentTarget.closest('.widget');
        const widgetId = id || (widgetWrapper ? widgetWrapper.id : '');
        const rect = e.currentTarget.getBoundingClientRect();
        const offsetX = e.clientX - rect.left;
        const offsetY = e.clientY - rect.top;

        let dragData = `environmental-status;${widgetId};${offsetX};${offsetY};`;
        dragData += widgetId.startsWith('base') ? 'add' : 'move';
        e.dataTransfer.setData('text/plain', dragData);
      }}
      style={{
        cursor: draggable ? 'grab' : 'default',
        borderRadius: widgetDims.cardRadius,
        '--esw-card-radius': widgetDims.cardRadius,
        '--esw-surface-radius': widgetDims.surfaceRadius,
        '--esw-padding-x': widgetDims.paddingX,
        '--esw-padding-y': widgetDims.paddingY,
        '--esw-topbar-gap': widgetDims.topbarGap,
        '--esw-grid-gap': widgetDims.gridGap,
        '--esw-title-size': widgetDims.titleFontSize,
        '--esw-title-max-width': widgetDims.titleMaxWidth,
        '--esw-columns': widgetDims.columns,
        '--esw-metric-padding-x': widgetDims.metricPaddingX,
        '--esw-divider-inset': widgetDims.dividerInset,
        '--esw-label-to-icon-gap': widgetDims.labelToIconGap,
        '--esw-label-size': widgetDims.labelFontSize,
        '--esw-label-height': widgetDims.labelHeight,
        '--esw-label-width': widgetDims.labelWidth,
        '--esw-label-padding-x': widgetDims.labelPaddingX,
        '--esw-label-padding-y': widgetDims.labelPaddingY,
        '--esw-bubble-size': widgetDims.bubbleSize,
        '--esw-icon-size': widgetDims.iconSize,
        '--esw-value-size': widgetDims.valueFontSize,
        '--esw-unit-size': widgetDims.unitFontSize,
        '--esw-metric-top-gap': widgetDims.metricTopGap,
        '--esw-metric-value-gap': widgetDims.metricValueGap,
        '--esw-switch-scale': widgetDims.switchScale,
        ...(String(titleColor || '').trim() ? { '--esw-title-color': titleColor } : {}),
      }}
    >
      <div className="environmental-status-widget__inner">
        <div className="environmental-status-widget__topbar">
          <h3 className="environmental-status-widget__title">{widgetTitle}</h3>

          {showControlSwitch ? (
            <div
              className="environmental-status-widget__controlSwitch"
              onMouseDown={(e) => e.stopPropagation()}
              onDragStart={(e) => e.preventDefault()}
            >
              <CustomSwitch
                checked={controlChecked}
                disabled={!canControl}
                onChange={handleControlSwitchChange}
              />
            </div>
          ) : null}
        </div>

        {hasMetrics ? (
          <div className="environmental-status-widget__metricsRow">
            {resolvedMetrics.map((metric, index) => (
              <section
                key={metric.id}
                className={[
                  'environmental-status-widget__metric',
                  showDividers && index > 0 && (metricLayoutSpans[index] || 1) < widgetDims.columns
                    ? 'has-divider'
                    : '',
                ].join(' ')}
                style={{
                  gridColumn: `span ${metricLayoutSpans[index] || 1}`,
                }}
              >
                <div className="environmental-status-widget__metricTop">
                  <span
                    className="environmental-status-widget__metricLabel"
                    style={{
                      '--esw-accent': metric.labelColor,
                      '--esw-accent-soft': metric.labelBackgroundColor,
                    }}
                    title={metric.label}
                  >
                    {widgetDims.abbreviateLabels ? getCompactMetricLabel(metric) : metric.label}
                  </span>

                  <span
                    className="environmental-status-widget__metricIconBubble"
                    style={{
                      '--esw-accent': metric.iconColor,
                      '--esw-accent-soft': metric.iconBackgroundColor,
                      '--esw-accent-shadow': colorWithAlpha(metric.iconColor, '55'),
                    }}
                  >
                    <MetricIcon
                      iconType={metric.iconType}
                      datastreamIcon={metric.datastreamIcon}
                      accentColor={metric.iconColor}
                      label={metric.label}
                    />
                  </span>
                </div>

                <div className="environmental-status-widget__metricValueWrap">
                  <span
                    className="environmental-status-widget__metricValue"
                    style={{ color: metric.valueColor }}
                  >
                    {metric.value}
                  </span>

                  {metric.unit ? (
                    <span
                      className="environmental-status-widget__metricUnit"
                      style={{ color: metric.unitColor }}
                    >
                      {metric.unit}
                    </span>
                  ) : null}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div className="environmental-status-widget__empty">
            Add a datastream to show environmental status.
          </div>
        )}
      </div>
    </div>
  );
};

export default EnvironmentalStatus;
